/**
 * Overtime History Content (Tab İçeriği)
 * 
 * Fazla mesai geçmişini gösteren tab içeriği.
 * OvertimeManagement sayfasında tab olarak kullanılır.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { message, DatePicker, Button, Pagination, Table } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/tr';
import locale from 'antd/lib/date-picker/locale/tr_TR';

// Services
import overtimeService from '../../../../services/overtime/overtime.service';
import { useDepartments } from '../../../../hooks/useDepartments';
import { useUsers } from '../../../../hooks/useUsers';

// Types
import { OvertimeDto, OvertimeFilters, OvertimeStatus } from '../../../../types/overtime.types';
import { UserStatus } from '../../../../types/user.types';

// Components
import OvertimeStatusBadge from './OvertimeStatusBadge';
import CommonSelect from '../../../../core/common/commonSelect';
import LoadingSpinner from '../../../shared/uiInterface/base-ui/LoadingSpinner';

// Utils
import { handleApiError } from '../../../../utils/errorHandler';
import { exportToExcel, createPdf } from '../../../../utils/exportUtils';
import { STATUS_FILTER_OPTIONS, OVERTIME_PAGINATION_DEFAULTS } from '../../../../constants/overtimeConstants';

const { RangePicker } = DatePicker;

/**
 * Geçmiş Tab İçeriği
 */
const OvertimeHistoryContent: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { departments } = useDepartments();
  const { users } = useUsers();

  // State'ler
  const [overtimes, setOvertimes] = useState<OvertimeDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(OVERTIME_PAGINATION_DEFAULTS.perPage);
  
  // Filtreler
  const [departmentFilter, setDepartmentFilter] = useState<number>(0);
  const [employeeFilter, setEmployeeFilter] = useState<number>(0);
  const [statusFilter, setStatusFilter] = useState<'all' | OvertimeStatus>('all');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);

  /**
   * Overtime kayıtlarını getir
   */
  const fetchOvertimes = useCallback(async () => {
    setLoading(true);
    try {
      const filters: OvertimeFilters = {
        departmentId: departmentFilter > 0 ? departmentFilter : undefined,
        employeeId: employeeFilter > 0 ? employeeFilter : undefined,
        status: statusFilter,
        startDate: dateRange ? dateRange[0].format('YYYY-MM-DD') : undefined,
        endDate: dateRange ? dateRange[1].format('YYYY-MM-DD') : undefined,
        page: currentPage,
        perPage: pageSize,
      };

      const result = await overtimeService.getAllOvertimes(filters);
      setOvertimes(result.data);
      setTotalRecords(result.pagination.total);
    } catch (error: any) {
      if (error?.response?.status === 404 || error?.message?.includes('404')) {
        message.warning(t('overtime.endpointNotReady', 'Fazla mesai geçmişi backend\'de henüz aktif değil'));
      } else {
        handleApiError(error, t);
      }
    } finally {
      setLoading(false);
    }
  }, [departmentFilter, employeeFilter, statusFilter, dateRange, currentPage, pageSize, t]);

  // Filtreler değiştiğinde verileri getir
  useEffect(() => {
    fetchOvertimes();
  }, [fetchOvertimes]);

  /**
   * Verileri export için formatla
   */
  const getFormattedExportData = () => {
    return overtimes.map(overtime => ({
      tarih: dayjs(overtime.date).format('DD.MM.YYYY'),
      calisan: `${overtime.user.firstName} ${overtime.user.lastName}`,
      departman: overtime.user.primaryDepartment?.name || '-',
      baslangic: overtime.overtimeStart,
      bitis: overtime.overtimeEnd,
      sure: `${Math.floor(overtime.overtimeDuration / 60)} saat ${overtime.overtimeDuration % 60} dakika`,
      durum: overtime.status === OvertimeStatus.APPROVED 
        ? 'Onayli' 
        : overtime.status === OvertimeStatus.REJECTED 
        ? 'Reddedildi' 
        : 'Beklemede',
      aciklama: overtime.description || '-',
      olusturan: overtime.createdBy === 'employee' ? 'Calisan' : 'Yonetici',
    }));
  };

  /**
   * Excel export
   */
  const handleExcelExport = () => {
    try {
      const formattedData = getFormattedExportData();
      const columns = [
        { header: 'Tarih', property: 'tarih' as const, width: 15 },
        { header: 'Çalışan', property: 'calisan' as const, width: 25 },
        { header: 'Departman', property: 'departman' as const, width: 20 },
        { header: 'Başlangıç', property: 'baslangic' as const, width: 12 },
        { header: 'Bitiş', property: 'bitis' as const, width: 12 },
        { header: 'Süre', property: 'sure' as const, width: 15 },
        { header: 'Durum', property: 'durum' as const, width: 15 },
        { header: 'Açıklama', property: 'aciklama' as const, width: 30 },
        { header: 'Oluşturan', property: 'olusturan' as const, width: 15 },
      ];

      exportToExcel(formattedData, 'Fazla Mesai Geçmişi', columns);
      message.success(t('overtime.exportSuccess', 'Excel dosyası başarıyla indirildi'));
    } catch (error) {
      message.error(t('overtime.exportError', 'Excel export sırasında bir hata oluştu'));
    }
  };

  /**
   * PDF export - Türkçe karakter desteği ile
   */
  const handlePDFExport = async () => {
    try {
      const formattedData = getFormattedExportData();
      
      // İstatistikler
      const approvedCount = overtimes.filter(o => o.status === OvertimeStatus.APPROVED).length;
      const pendingCount = overtimes.filter(o => o.status === OvertimeStatus.PENDING).length;
      const rejectedCount = overtimes.filter(o => o.status === OvertimeStatus.REJECTED).length;
      
      // PDF için kolon ayarları - Türkçe karakterler yerine normal karakterler
      const pdfColumns = [
        { header: 'Tarih', property: 'tarih', width: 22, align: 'center' as const },
        { header: 'Calisan', property: 'calisan', width: 35, align: 'left' as const },
        { header: 'Departman', property: 'departman', width: 30, align: 'left' as const },
        { header: 'Baslangic', property: 'baslangic', width: 22, align: 'center' as const },
        { header: 'Bitis', property: 'bitis', width: 20, align: 'center' as const },
        { header: 'Sure', property: 'sure', width: 28, align: 'center' as const },
        { header: 'Durum', property: 'durum', width: 25, align: 'center' as const },
        { header: 'Aciklama', property: 'aciklama', width: 45, align: 'left' as const },
        { header: 'Olusturan', property: 'olusturan', width: 22, align: 'center' as const },
      ];

      await createPdf(formattedData, {
        title: 'Fazla Mesai Gecmisi',
        description: `${dateRange ? dateRange[0].format('DD.MM.YYYY') + ' - ' + dateRange[1].format('DD.MM.YYYY') : 'Tum Tarihler'}`,
        headerColor: '#1e40af',
        accentColor: '#3b82f6',
        companyName: 'DINOX',
        columns: pdfColumns,
        showStatistics: true,
        statistics: [
          { label: 'Toplam Kayit', value: totalRecords },
          { label: 'Onayli', value: approvedCount },
          { label: 'Bekleyen', value: pendingCount },
          { label: 'Reddedilen', value: rejectedCount }
        ],
        footer: {
          text: `(c) ${new Date().getFullYear()} DINOX - Fazla Mesai Yonetim Sistemi`,
          showPageNumbers: true
        }
      });
      
      message.success(t('overtime.pdfExportSuccess', 'PDF dosyası başarıyla indirildi'));
    } catch (error) {
      console.error('PDF export error:', error);
      message.error(t('overtime.pdfExportError', 'PDF export sırasında bir hata oluştu'));
    }
  };

  // Departman seçenekleri
  const departmentOptions = [
    { value: 0, label: t('overtime.allDepartments', 'Tüm Departmanlar') },
    ...departments.map(dept => ({
      value: dept.id!,
      label: dept.name
    }))
  ];

  // Çalışan seçenekleri - Sadece aktif çalışanlar
  const employeeOptions = [
    { value: 0, label: t('overtime.allEmployees', 'Tüm Çalışanlar') },
    ...users
      .filter(user => user.status === UserStatus.ACTIVE) // Sadece aktif çalışanlar
      .map(user => ({
        value: user.id!,
        label: `${user.firstName} ${user.lastName}`
      }))
  ];

  // Tablo kolonları - Ant Design Table için
  const columns: any[] = [
    {
      title: t('overtime.table.date', 'Tarih'),
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY'),
      sorter: (a: OvertimeDto, b: OvertimeDto) => dayjs(a.date).unix() - dayjs(b.date).unix(),
      width: 120,
    },
    {
      title: t('overtime.table.employee', 'Çalışan'),
      dataIndex: 'user',
      key: 'employee',
      render: (_: any, record: OvertimeDto) => (
        <span>{record.user.firstName} {record.user.lastName}</span>
      ),
      width: 180,
    },
    {
      title: t('overtime.table.department', 'Departman'),
      dataIndex: 'user',
      key: 'department',
      render: (_: any, record: OvertimeDto) => record.user.primaryDepartment?.name || '-',
      width: 150,
    },
    {
      title: t('overtime.table.time', 'Saat'),
      key: 'time',
      render: (_: any, record: OvertimeDto) => (
        <span>{record.overtimeStart} - {record.overtimeEnd}</span>
      ),
      width: 150,
    },
    {
      title: t('overtime.table.duration', 'Süre'),
      dataIndex: 'overtimeDuration',
      key: 'duration',
      render: (duration: number) => (
        <span>{Math.floor(duration / 60)} sa {duration % 60} dk</span>
      ),
      sorter: (a: OvertimeDto, b: OvertimeDto) => a.overtimeDuration - b.overtimeDuration,
      width: 120,
    },
    {
      title: t('overtime.table.status', 'Durum'),
      dataIndex: 'status',
      key: 'status',
      render: (status: OvertimeStatus) => <OvertimeStatusBadge status={status} />,
      width: 120,
    },
    {
      title: t('overtime.table.createdBy', 'Oluşturan'),
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (createdBy: string) => (
        <span>
          {createdBy === 'employee' 
            ? t('overtime.employee', 'Çalışan')
            : t('overtime.manager', 'Yönetici')
          }
        </span>
      ),
      width: 120,
    },
  ];

  return (
    <div className="overtime-history-content">
      {/* Filtreler Card */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3">
            {/* Departman Filtresi */}
            <div className="col-lg-3 col-md-6 col-sm-12">
              <label className="form-label fw-medium">
                <i className="ti ti-building me-2" />
                {t('overtime.filters.department', 'Departman')}
              </label>
              <CommonSelect
                options={departmentOptions}
                value={departmentOptions.find(opt => opt.value === departmentFilter)}
                onChange={(selected: any) => {
                  setDepartmentFilter(selected?.value);
                  setCurrentPage(1);
                }}
                className="select"
              />
            </div>

            {/* Çalışan Filtresi */}
            <div className="col-lg-3 col-md-6 col-sm-12">
              <label className="form-label fw-medium">
                <i className="ti ti-user me-2" />
                {t('overtime.filters.employee', 'Çalışan')}
              </label>
              <CommonSelect
                options={employeeOptions}
                value={employeeOptions.find(opt => opt.value === employeeFilter)}
                onChange={(selected: any) => {
                  setEmployeeFilter(selected?.value);
                  setCurrentPage(1);
                }}
                className="select"
              />
            </div>

            {/* Durum Filtresi */}
            <div className="col-lg-2 col-md-6 col-sm-12">
              <label className="form-label fw-medium">
                <i className="ti ti-flag me-2" />
                {t('overtime.filters.status', 'Durum')}
              </label>
              <CommonSelect
                options={STATUS_FILTER_OPTIONS}
                value={STATUS_FILTER_OPTIONS.find(opt => opt.value === statusFilter)}
                onChange={(selected: any) => {
                  setStatusFilter(selected?.value);
                  setCurrentPage(1);
                }}
                className="select"
              />
            </div>

            {/* Tarih Aralığı */}
            <div className="col-lg-4 col-md-6 col-sm-12">
              <label className="form-label fw-medium">
                <i className="ti ti-calendar me-2" />
                {t('overtime.filters.dateRange', 'Tarih Aralığı')}
              </label>
              <div style={{ width: '100%' }}>
                <RangePicker
                  value={dateRange}
                  onChange={(dates) => {
                    setDateRange(dates as [Dayjs, Dayjs] | null);
                    setCurrentPage(1);
                  }}
                  format="DD/MM/YYYY"
                  locale={i18n.language === 'tr' ? locale : undefined}
                  style={{ width: '100%', height: '38px' }}
                  placeholder={[
                    t('overtime.filters.startDate', 'Başlangıç'),
                    t('overtime.filters.endDate', 'Bitiş')
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Aksiyon Butonları Card */}
      <div className="card mb-3">
        <div className="card-body py-2">
          {/* Sadece butonlar - sağa hizalı */}
          <div className="d-flex justify-content-end gap-2 flex-wrap">
            <Button
              icon={<i className="ti ti-reload me-2" />}
              onClick={fetchOvertimes}
              loading={loading}
              size="middle"
              style={{ backgroundColor: '#f8f9fa', borderColor: '#dee2e6', color: '#6c757d' }}
            >
              {t('common.buttons.refresh', 'Yenile')}
            </Button>
            <Button
              type="primary"
              icon={<i className="ti ti-file-spreadsheet me-2" />}
              onClick={handleExcelExport}
              disabled={overtimes.length === 0}
              size="middle"
            >
              {t('common.buttons.excel', 'Excel İndir')}
            </Button>
            <Button
              type="primary"
              icon={<i className="ti ti-file-type-pdf me-2" />}
              onClick={handlePDFExport}
              disabled={overtimes.length === 0}
              size="middle"
              style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
            >
              {t('common.buttons.pdf', 'PDF İndir')}
            </Button>
          </div>
        </div>
      </div>

      {/* Tablo Card */}
      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="p-5">
              <LoadingSpinner centered={true} />
            </div>
          ) : overtimes.length === 0 ? (
            <div className="p-5 text-center">
              <div className="mb-3">
                <i className="ti ti-file-off" style={{ fontSize: '48px', color: '#d9d9d9' }} />
              </div>
              <p className="text-muted mb-0">
                {t('overtime.noRecords', 'Kayıt bulunamadı')}
              </p>
            </div>
          ) : (
            <>
              {/* 
                Ant Design Table kullanıyoruz
                pagination={false} çünkü aşağıda özel pagination var
                "Row Per Page" kısmı tamamen kaldırıldı
              */}
              <div className="table-responsive">
                <Table
                  dataSource={overtimes}
                  columns={columns}
                  pagination={false}
                  rowKey="id"
                  className="table-striped"
                  scroll={{ x: 'max-content' }}
                />
              </div>

            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OvertimeHistoryContent;

